export const MATERIAL_LEVELS = [
  { code: 1, name: "资产", category: "asset" },
  { code: 2, name: "低值易耗", category: "accessory" },
  { code: 3, name: "耗材", category: "consumable" },
  { code: 4, name: "无形资产", category: "software" },
  { code: 5, name: "软件", category: "software" },
] as const;

export type MaterialLevelCode = (typeof MATERIAL_LEVELS)[number]["code"];
export type MaterialCategory = (typeof MATERIAL_LEVELS)[number]["category"];

export const CATEGORY_CODES = "ABCDEFGHJKLMNPQRSTUVWXYZ".split("");
export const PRODUCT_CODES = "123456789".split("");
export const SPEC_CODES = "ABCDEFGHJKLMNPQRSTUVWXYZ".split("");
export const UNSPECIFIED_SPEC_CODE = "Z";

export function materialCategoryForLevel(levelCode: number): MaterialCategory {
  const level = MATERIAL_LEVELS.find((item) => item.code === levelCode);
  if (!level) throw new RangeError(`未知物料级别：${levelCode}`);
  return level.category;
}

export interface ClassificationInput {
  levelCode: number;
  estimatedUnitPriceCents: number | null;
  expectedLifeDays: number;
}

export interface ClassificationResult {
  ok: boolean;
  category: MaterialCategory;
  reasons: string[];
}

/**
 * SOP 5.1 / design §3.1. The level is chosen by the user; this function only
 * checks whether the price/life inputs are consistent with that level. Software
 * levels 4/5 are exempt from the asset/accessory/consumable thresholds.
 */
export function validateMaterialClassification(input: ClassificationInput): ClassificationResult {
  if (!Number.isInteger(input.expectedLifeDays) || input.expectedLifeDays <= 0) {
    throw new RangeError("预计使用期必须是正整数天数");
  }
  const category = materialCategoryForLevel(input.levelCode);
  if (category === "software") {
    return { ok: true, category, reasons: [] };
  }
  if (input.estimatedUnitPriceCents === null) {
    return {
      ok: false,
      category,
      reasons: ["请填写预估单价，用于分类阈值校验"],
    };
  }
  if (input.estimatedUnitPriceCents < 0) {
    throw new RangeError("预估单价不能为负数");
  }

  const reasons: string[] = [];
  if (category === "asset") {
    if (input.estimatedUnitPriceCents < 200_000) {
      reasons.push("资产预估单价应大于或等于 2000 元");
    }
  } else if (category === "accessory") {
    const priceInRange =
      input.estimatedUnitPriceCents >= 50_000 && input.estimatedUnitPriceCents < 200_000;
    const longLife = input.expectedLifeDays > 365;
    if (!priceInRange && !longLife) {
      reasons.push("低值易耗应满足单价 500–2000 元，或预计使用期超过 12 个月");
    }
  } else if (category === "consumable") {
    if (input.estimatedUnitPriceCents >= 50_000) {
      reasons.push("耗材单价应小于 500 元");
    }
    if (input.expectedLifeDays > 365) {
      reasons.push("耗材预计使用期应不超过 12 个月");
    }
  }

  return { ok: reasons.length === 0, category, reasons };
}

export interface MaterialCodeInput {
  levelCode: number;
  categoryCode: string;
  productCode: string;
  specCode?: string | null;
}

function assertCode(value: string, allowed: readonly string[], label: string): string {
  if (!allowed.includes(value)) {
    throw new RangeError(`${label}编码不合法：${value}`);
  }
  return value;
}

/** Four-level material type code: level + category + product + spec (Z if none). */
export function buildMaterialCode(input: MaterialCodeInput): string {
  if (!Number.isInteger(input.levelCode) || input.levelCode < 1 || input.levelCode > 9) {
    throw new RangeError("物料级别必须是 1–9");
  }
  const category = assertCode(input.categoryCode, CATEGORY_CODES, "类别");
  const product = assertCode(input.productCode, PRODUCT_CODES, "品名");
  const spec =
    input.specCode === null || input.specCode === undefined
      ? UNSPECIFIED_SPEC_CODE
      : assertCode(input.specCode, SPEC_CODES, "规格配置");
  return `${input.levelCode}${category}${product}${spec}`;
}

export type CodeKind = "category" | "product" | "spec";

export function availableCodes(kind: CodeKind, used: readonly string[]): string[] {
  const universe =
    kind === "category" ? CATEGORY_CODES : kind === "product" ? PRODUCT_CODES : SPEC_CODES;
  const usedSet = new Set(used);
  return universe.filter((code) => !usedSet.has(code));
}

export function nextAvailableCode(kind: CodeKind, used: readonly string[]): string | null {
  return availableCodes(kind, used)[0] ?? null;
}
